import { collection, doc, getDoc, getDocs, query, where, limit, setDoc, serverTimestamp, Timestamp, FieldValue, type DocumentSnapshot, type DocumentData } from 'firebase/firestore';
import { definedOnly } from './definedOnly';
import { publicProfileUpdates } from './publicProfileFields';
import { db } from './config';

export interface MissionPage {
  id: string;
  userId: string;
  headerImage: string;
  title: string;
  description: string;
  donationImpact?: string;
  // TSF-238: users/{uid} is `allow get: if isSignedIn()`, so a logged-out
  // donor can never read the creator's profile photo from there. Mirrored
  // here (publicly readable) whenever the creator uploads one, same pattern
  // as headerImage/description.
  photoURL?: string;
  isPublished: boolean;
  // MSF-222: a page can now be published before Stripe onboarding, so a live
  // page may belong to someone with nowhere to send the money. users/{uid} is
  // not readable logged-out, so this mirrors "the creator has a connected
  // account" onto the publicly readable missionpages doc, where the donate form
  // can read it. Absent -- every page published before this shipped -- means
  // "assume giving works": never hide a donate button we are not sure is dead.
  givingEnabled?: boolean;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
}

export interface Mission {
  id: string;
  slug: string;
  title: string;
  headerImage: string;
  description: string;
  isPublished: boolean;
  userId: string;
  createdAt: Timestamp | FieldValue;
  updatedAt: Timestamp | FieldValue;
}

export async function saveMissionPage(userId: string, data: Omit<MissionPage, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) {
  try {
    const pageRef = doc(db, 'missionpages', userId);

    // Get existing document to preserve timestamps
    const existingDoc = await getDoc(pageRef);
    const existingData = existingDoc.exists() ? existingDoc.data() : {};

    const docData = {
      ...existingData,
      userId,
      // Only write what the caller supplied: a partial save must not blank a live page.
      ...definedOnly({
        headerImage: data.headerImage,
        title: data.title,
        description: data.description,
        donationImpact: data.donationImpact,
        isPublished: data.isPublished,
      }),
      updatedAt: serverTimestamp(),
      createdAt: existingData.createdAt || serverTimestamp(),
    };

    await setDoc(pageRef, docData);
    return userId;
  } catch (error) {
    console.error('Error saving fund page:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to save fund page');
  }
}

function missionPageFromSnapshot(pageDoc: DocumentSnapshot<DocumentData>): MissionPage {
  const data = pageDoc.data();
  return {
    id: pageDoc.id,
    userId: pageDoc.id,
    headerImage: data.headerImage || '',
    title: data.title || '',
    description: data.description || '',
    donationImpact: data.donationImpact || '',
    photoURL: data.photoURL || '',
    isPublished: data.isPublished || false,
    givingEnabled: data.givingEnabled,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt
  } as MissionPage;
}

export async function getMissionPage(userId: string): Promise<MissionPage | null> {
  try {
    const pageDoc = await getDoc(doc(db, 'missionpages', userId));
    if (!pageDoc.exists()) return null;
    return missionPageFromSnapshot(pageDoc);
  } catch (error) {
    console.error('Error fetching fund page:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to fetch fund page');
  }
}

/**
 * Fast path for the public fund page: when the URL identifier is already a
 * Firebase UID, resolveUserIdFromIdentifier() (lib/firebase/user.ts) fetches
 * missionpages/{uid} once just to confirm it exists, then discards the data
 * -- MissionPage.tsx used to fetch the exact same document a second time via
 * getMissionPage() right after. Fetches it once and hands the mapped doc
 * back so the page can skip the duplicate round trip on its highest-traffic
 * route. Returns null for a slug identifier or a UID with no page, so
 * callers fall back to the existing resolve+fetch path unchanged.
 */
export async function getMissionPageIfUid(identifier: string): Promise<{ uid: string; missionPage: MissionPage } | null> {
  if (!/^[A-Za-z0-9_-]{20,}$/.test(identifier)) return null;
  try {
    const pageDoc = await getDoc(doc(db, 'missionpages', identifier));
    if (!pageDoc.exists()) return null;
    return { uid: identifier, missionPage: missionPageFromSnapshot(pageDoc) };
  } catch {
    return null;
  }
}

/**
 * TSF-238 from the other end. The backfill fixed the pages that already
 * existed; without this every NEW creator recreated the bug the moment they
 * published, because publishMissionPage wrote isPublished and nothing else --
 * so a freshly published page had no title, no photo and no story on the
 * publicly readable doc, and a logged-out donor saw a grey silhouette.
 * Best-effort: a failure here must never take down a publish.
 */
async function mirrorPublicProfile(userId: string) {
  try {
    const [pageSnap, userSnap] = await Promise.all([
      getDoc(doc(db, 'missionpages', userId)),
      getDoc(doc(db, 'users', userId)),
    ]);
    if (!userSnap.exists()) return;
    const updates = publicProfileUpdates(
      userSnap.data() as Record<string, any>,
      pageSnap.exists() ? (pageSnap.data() as Record<string, any>) : {}
    );
    if (Object.keys(updates).length === 0) return;
    await setDoc(doc(db, 'missionpages', userId), { ...updates, updatedAt: serverTimestamp() }, { merge: true });
  } catch (error) {
    console.warn('[publishMissionPage] could not mirror public profile', error);
  }
}

export async function publishMissionPage(userId: string, publish: boolean = true) {
  try {
    const pageRef = doc(db, 'missionpages', userId);
    await setDoc(pageRef, {
      isPublished: publish,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    // Going live is the moment the public doc has to stand on its own.
    if (publish) await mirrorPublicProfile(userId);
  } catch (error) {
    console.error('Error publishing fund page:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to publish fund page');
  }
}

/**
 * Mirror "this creator has somewhere to send the money" onto the publicly
 * readable page doc (MSF-222). Owner-only write, best-effort: a failure here
 * must never take down a publish or a dashboard load.
 */
export async function setGivingEnabled(userId: string, enabled: boolean) {
  try {
    const ref = doc(db, 'missionpages', userId);

    // Write ONLY when the value actually changes.
    //
    // The dashboard fires this on every load. A pending Firestore write makes
    // the SDK serve its own local mutation as the document to any CONCURRENT
    // read -- and before the server copy is cached, that local view is just the
    // written fields. getMissionPage(), running alongside, therefore received
    // { givingEnabled, updatedAt } with no isPublished, and the dashboard told
    // a creator whose page was live "your page is not published -- nobody can
    // see it or give to it yet". She re-published by hand on every visit and
    // wrote in to report it.
    //
    // Proven by disabling this one call and re-reading: the same read went from
    //   keys=givingEnabled,updatedAt        isPublished=undefined
    // to
    //   keys=title,isPublished,givingEnabled,updatedAt   isPublished=true
    //
    // getDocFromServer() does NOT help -- pending local mutations are overlaid
    // on that result too. Not issuing the no-op write is the fix, and it also
    // stops a pointless write on every dashboard load.
    const existing = await getDoc(ref);
    if (existing.exists() && existing.data()?.givingEnabled === enabled) return;

    await setDoc(ref, {
      givingEnabled: enabled,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.warn('[setGivingEnabled] could not mirror giving status', error);
  }
}

/**
 * Mirror a public-facing profile field onto the publicly readable missionpages
 * doc (TSF-238: photo and biography were saved only to users/{uid}, which is
 * `allow get: if isSignedIn()` -- so a logged-out donor got a 403 and the page
 * showed a silhouette, a gradient and boilerplate even though everything was
 * set). merge:true so this can never clobber headerImage/title/description/
 * isPublished the way saveMissionPage's full-overwrite pattern would.
 */
export async function setPublicProfileFields(
  userId: string,
  fields: Partial<Pick<MissionPage, 'photoURL' | 'description' | 'headerImage'>>
) {
  try {
    await setDoc(doc(db, 'missionpages', userId), {
      ...fields,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  } catch (error) {
    console.warn('[setPublicProfileFields] could not mirror public profile field', error);
  }
}

export async function getPublishedMissionPages(max: number = 100): Promise<MissionPage[]> {
  try {
    // Single-field equality filter only (no orderBy) so this doesn't need a new
    // composite index — sorted client-side instead, since deploying a new
    // firestore.indexes.json entry goes through the same IAM-gated pipeline as
    // firestore.rules (see docs/PRIORITY.md "Standing status").
    const pagesRef = collection(db, 'missionpages');
    const q = query(pagesRef, where('isPublished', '==', true), limit(max));
    const snapshot = await getDocs(q);
    const pages = snapshot.docs.map((docSnap) => {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        userId: docSnap.id,
        headerImage: data.headerImage || '',
        title: data.title || '',
        description: data.description || '',
        donationImpact: data.donationImpact || '',
        isPublished: data.isPublished || false,
      givingEnabled: data.givingEnabled,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as MissionPage;
    });
    pages.sort((a, b) => {
      const aMs = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : 0;
      const bMs = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : 0;
      return aMs - bMs;
    });
    return pages;
  } catch (error) {
    console.error('Error fetching published fund pages:', error);
    return [];
  }
}

export const getMissionBySlug = async (slug: string): Promise<Mission | null> => {
  try {
    const missionRef = collection(db, 'missions');
    const q = query(missionRef, where('slug', '==', slug));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const missionData = querySnapshot.docs[0].data() as Mission;
    return missionData;
  } catch (error) {
    console.error('[getMissionBySlug] Error fetching fund page:', error);
    throw error;
  }
};

export { publicProfileUpdates } from './publicProfileFields';

export { definedOnly } from './definedOnly';
